/**
 * auditoriaLabels.js
 * ==================
 * Mapas de rutas → nombres funcionales de pantalla.
 *
 * Usado en:
 *  - api/fetchInterceptor.js          (header X-Pantalla con label funcional)
 *  - screens/ReporteAuditoriaScreen/TablaAuditoria.jsx  (display)
 *  - utils/exportReporteAuditoriaXLS.js                 (exportación Excel)
 */

/**
 * Ruta React (pathname) → nombre funcional de pantalla.
 * Debe mantenerse sincronizado con las rutas definidas en src/index.jsx.
 */
export const ROUTE_LABEL_MAP = {
  // General
  '/inicio':                            'Pantalla de Inicio',
  '/login':                             'Inicio de Sesión',
  '/recuperar':                         'Recuperación de Contraseña',
  '/restablecer-contrasena':            'Restablecer Contraseña',
  // Consulta
  '/consulta/estudiantes':              'Consulta de Estudiantes',
  // Control Escolar
  '/programas':                         'Programas Académicos',
  '/materias':                          'Materias',
  '/asignacion-programa-materia':       'Asignación Programa–Materia',
  '/estudiantes':                       'Gestión de Estudiantes',
  '/calificaciones-escolar':            'Calificaciones Escolar',
  '/reporte-calificaciones':            'Reporte de Calificaciones',
  '/constancias':                       'Constancias',
  // Perfil Académico
  '/grupos':                            'Grupos',
  '/docentes':                          'Asesores / Docentes',
  '/preasignacion-materias':            'Preasignación de Materias',
  '/asignacion-grupos':                 'Asignación de Grupos',
  '/reporte-carga-academica':           'Reporte Carga Académica',
  '/reporte-estudiantes-carrera':       'Reporte Estudiantes por Carrera',
  '/materias-equivalentes-revalidar':   'Materias Equivalentes / Revalidar',
  // Panel Asesor
  '/firma-concentrados':                'Rúbrica',
  '/registro-calificaciones':           'Registro de Calificaciones',
  '/registro-asistencias':              'Registro de Asistencias',
  // Administración del Sistema – Catálogos
  '/instituciones':                     'Instituciones',
  '/campus':                            'Campus',
  '/secciones-academicas':              'Secciones Académicas',
  '/periodos':                          'Períodos Académicos',
  '/ciclos-escolares':                  'Ciclos Escolares',
  '/conceptos-cobro':                   'Conceptos de Cobro',
  '/becas':                             'Becas',
  '/academias':                         'Academias',
  '/gestion-usuarios':                  'Gestión de Usuarios',
  '/roles-permisos':                    'Roles y Permisos',
  '/reporte-becas':                     'Reporte de Becas',
  '/reporte-estudiantes-inscritos':     'Reporte Estudiantes Inscritos',
  '/reporte-estudiantes-nuevo-ingreso': 'Reporte Nuevos Ingresos',
  '/reporte-eficiencia-terminal':       'Reporte de Eficiencia Terminal',
  '/tramites-escolares':                'Trámites Escolares',
  '/reporte-rectoria':                  'Reporte Rectoría',
  '/reporte-auditoria':                 'Reporte Auditoría',
  // Finanzas
  '/inscripcion-reinscripcion':         'Inscripción / Reinscripción',
  '/asignacion-becas':                  'Asignación de Becas',
  '/generacion-cargos-manuales':        'Cargos Manuales',
  '/aplicacion-pagos':                  'Aplicación de Pagos',
  '/reporte-ingresos-diarios':          'Reporte Ingresos Diarios',
  '/reporte-adeudos':                   'Reporte de Adeudos',
  '/generacion-cargos-automaticos':     'Cargos Automáticos',
  '/reportes-financieros':              'Reportes Financieros',
  '/inscripcion-nuevo':                 'Inscripción Nuevo Ingreso',
  '/reporte-seguros-estudiantes':       'Reporte Seguros Estudiantes',
  '/reporte-seguros-asesores':          'Reporte Seguros Asesores',
  // Admisiones
  '/admision-estudiantes':              'Admisión de Estudiantes',
  '/gestion-interesados':               'Gestión de Interesados',
  // Otros
  '/cambio-grupos':                     'Cambio de Grupos',
  '/baja-reactivacion':                 'Baja / Reactivación',
  '/cierre-ciclo-escolar':              'Cierre de Ciclo Escolar',
  '/portal-estudiantes':                'Portal de Estudiantes',
  '/reporte-grupos-estudiantes':        'Reporte Grupos-Estudiantes',
  '/reportes/grupos-estudiantes':       'Reporte Grupos-Estudiantes',
  '/build':                             'Build Management',
};

/**
 * Prefijos de ruta de API → nombre funcional de pantalla.
 * Usado para convertir rutas técnicas almacenadas en registros históricos.
 * Orden importa: el primer prefijo que coincida gana.
 */
const API_PREFIX_LABEL_MAP = [
  // Escolar
  ['/api/escolar/estudiantes-grupos',           'Asignación de Grupos'],
  ['/api/escolar/bajas-estudiantes',            'Baja / Reactivación'],
  ['/api/escolar/preasignaciones',              'Preasignación de Materias'],
  ['/api/escolar/interesados-seguimientos',     'Gestión de Interesados'],
  ['/api/escolar/interesados',                  'Gestión de Interesados'],
  ['/api/escolar/constancias',                  'Constancias'],
  ['/api/escolar/calificaciones',               'Calificaciones Escolar'],
  ['/api/escolar/asistencias',                  'Registro de Asistencias'],
  ['/api/escolar/rubricas',                     'Registro de Calificaciones'],
  ['/api/escolar/cardex',                       'Gestión de Estudiantes'],
  ['/api/escolar/planes-estudio',               'Asignación Programa–Materia'],
  ['/api/escolar/programas-academicos',         'Programas Académicos'],
  ['/api/escolar/programas-campus',             'Programas Académicos'],
  ['/api/escolar/materias-campus',              'Materias'],
  ['/api/escolar/materias-prerrequisitos',      'Materias'],
  ['/api/escolar/materias',                     'Materias'],
  ['/api/escolar/grupos-campus',                'Grupos'],
  ['/api/escolar/grupos-horarios',              'Grupos'],
  ['/api/escolar/grupos',                       'Grupos'],
  ['/api/escolar/asesores-campus',              'Asesores / Docentes'],
  ['/api/escolar/asesores-horarios',            'Asesores / Docentes'],
  ['/api/escolar/asesores',                     'Asesores / Docentes'],
  ['/api/escolar/inscripciones',                'Inscripción / Reinscripción'],
  ['/api/escolar/estudiantes',                  'Gestión de Estudiantes'],
  // Financiero
  ['/api/financiero/cargos-estudiantes',        'Cargos Manuales'],
  ['/api/financiero/aplicacion-pagos',          'Aplicación de Pagos'],
  ['/api/financiero/estudiantes-becas',         'Asignación de Becas'],
  ['/api/financiero/cierres-financieros',       'Reportes Financieros'],
  ['/api/financiero/conceptos-cobro',           'Conceptos de Cobro'],
  ['/api/financiero/becas',                     'Becas'],
  ['/api/financiero/pagos',                     'Aplicación de Pagos'],
  ['/api/financiero/webpay',                    'Aplicación de Pagos'],
  ['/api/financiero/',                          'Finanzas'],
  // Catálogos
  ['/api/catalogos/instituciones',              'Instituciones'],
  ['/api/catalogos/campus',                     'Campus'],
  ['/api/catalogos/secciones-academicas',       'Secciones Académicas'],
  ['/api/catalogos/tipos-periodo',              'Períodos Académicos'],
  ['/api/catalogos/ciclos-escolares',           'Ciclos Escolares'],
  ['/api/catalogos/ciclos-campus',              'Ciclos Escolares'],
  ['/api/catalogos/academias',                  'Academias'],
  ['/api/catalogos/roles',                      'Roles y Permisos'],
  // Sistema
  ['/api/sistema/usuarios',                     'Gestión de Usuarios'],
  ['/api/sistema/roles',                        'Roles y Permisos'],
  ['/api/sistema/permisos',                     'Roles y Permisos'],
  // Documental
  ['/api/documental/expedientes-estudiantes',   'Gestión de Estudiantes'],
  ['/api/documental/expedientes-asesores',      'Asesores / Docentes'],
  ['/api/documental/expedientes-interesados',   'Gestión de Interesados'],
  // Auth
  ['/api/token',                                'Inicio de Sesión'],
];

/**
 * Convierte cualquier valor del campo `pantalla` almacenado en la BD
 * en un nombre funcional legible para un usuario administrativo.
 *
 * Estrategia:
 *  1. Valor vacío / N/A / — → "Información no registrada"
 *  2. No empieza con '/' → ya es una etiqueta funcional (registros nuevos post-fix)
 *  3. Ruta React exacta o sin query-params → ROUTE_LABEL_MAP
 *  4. Prefijo de ruta de API → API_PREFIX_LABEL_MAP
 *  5. No reconocido → "Información no registrada"
 */
export function resolverNombrePantalla(valor) {
  if (!valor || valor === 'N/A' || valor === '—') {
    return 'Información no registrada';
  }

  const v = valor.trim();

  // Caso 1: ya es una etiqueta funcional (no es ruta)
  if (!v.startsWith('/')) return v;

  // Caso 2: ruta React exacta
  if (ROUTE_LABEL_MAP[v]) return ROUTE_LABEL_MAP[v];

  // Caso 3: ruta React con query-params o hash
  const basePath = v.split('?')[0].split('#')[0];
  if (ROUTE_LABEL_MAP[basePath]) return ROUTE_LABEL_MAP[basePath];

  // Caso 4: prefijo de ruta API
  for (const [prefix, label] of API_PREFIX_LABEL_MAP) {
    if (v.startsWith(prefix)) return label;
  }

  return 'Información no registrada';
}
